import { forwardRef } from "react";

import { DateTime } from "luxon";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { BaseFormDate, BaseFormDateProps } from "@/forms-design-system/FormDate/BaseFormDate";
import {
  formatTimeWithTimezone,
  getDateInTimezone,
  isMultiDayDate,
  shouldDisplayYear,
} from "@/forms-design-system/FormDate/helper";

export type SimpleFormDateRangeProps = Omit<BaseFormDateProps, "date" | "title" | "subtitle"> & {
  startDate: Date;
  endDate: Date;
  now?: Date;
  timezone?: string | null;
};

export const SimpleFormDateRange = forwardRef<HTMLDivElement, SimpleFormDateRangeProps>(
  ({ startDate, endDate, now, timezone, ...rest }, ref) => {
    const { t } = useTranslate();
    const { isoLocale } = useLocaleContext();
    const luxonNow = now ? getDateInTimezone(now, timezone) : DateTime.now().setZone(timezone || undefined);
    const luxonStartDate = getDateInTimezone(startDate, timezone);
    const luxonEndDate = getDateInTimezone(endDate, timezone);
    const multiDay = isMultiDayDate(luxonStartDate, luxonEndDate);

    const startTimeDisplay = luxonStartDate.toLocaleString(
      { hour: "numeric", minute: "2-digit" },
      { locale: isoLocale }
    );

    const endTimeDisplay = formatTimeWithTimezone(luxonEndDate, isoLocale);

    if (multiDay) {
      return (
        <BaseFormDate
          date={startDate}
          title={t("common", "dateRangeStartDate", {
            date: luxonStartDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonStartDate, luxonNow) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
              },
              { locale: isoLocale }
            ),
            time: startTimeDisplay,
          })}
          subtitle={t("common", "dateRangeEndDate", {
            date: luxonEndDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonEndDate, luxonNow) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
              },
              { locale: isoLocale }
            ),
            time: endTimeDisplay,
          })}
          {...rest}
          ref={ref}
        />
      );
    }

    return (
      <BaseFormDate
        date={startDate}
        title={luxonStartDate.toLocaleString(
          {
            month: "short",
            day: "numeric",
            year: shouldDisplayYear(luxonStartDate, luxonNow) ? "numeric" : undefined,
          },
          { locale: isoLocale }
        )}
        subtitle={t("common", "timeRange", {
          startTime: startTimeDisplay,
          endTime: endTimeDisplay,
        })}
        {...rest}
        ref={ref}
      />
    );
  }
);

SimpleFormDateRange.displayName = "SimpleFormDateRange";
